import type { ApprovalRequestStatus } from '../../approvals/approvalTypes';

/**
 * The purchase-request wire types.
 *
 * **THE ENUMS ARE NUMERIC, because that is what the API serializes.** No
 * `JsonStringEnumConverter` is configured, so a request body carrying `"urgency": "Normal"` is
 * refused outright — which is how the first version of this file failed, with a 400 naming
 * `$.urgency`. The convention is `ISSUANCE_TYPE`'s: an `as const` object of numbers, and the
 * LABEL comes from the server on the DTO rather than from a second map here.
 *
 * The FACET values are the exception and are strings — the server emits enum NAMES there and
 * parses them with `Enum.TryParse` — so a facet selection passes straight through untouched.
 */

/** How urgent a request is. The source's four, captured as-is. */
export const PR_URGENCY = {
  normal: 0,
  urgent: 1,
  critical: 2,
  emergency: 3,
} as const;

export type PurchaseRequestUrgency = (typeof PR_URGENCY)[keyof typeof PR_URGENCY];

/**
 * Where a request is in its own lifecycle.
 *
 * **6 and 7 LANDED with the purchase order and this file did not follow**, which is corrected
 * here. The comment read *"5, 6 and 7 are RESERVED server-side and deliberately absent… there
 * is no purchase order in this product"*, and that stopped being true on 29 Sep 2026: the PO
 * spends two of the three, and `PurchaseRequestUrgencies.Recompute` writes them.
 *
 * **Nothing was visibly broken, which is why it survived.** `statusLabel` is the server's, so
 * the badge read `fully ordered` correctly all along; what the client could not do was NAME
 * either state — every `row.status === PR_STATUS.x` comparison is simply false for a 6 or a 7,
 * which happens to be the right answer for Submit and Cancel and is luck rather than a rule.
 * The union is what lied, and `tsc` cannot see it: the wire value is asserted into this type at
 * the fetch boundary.
 *
 * **5 is still RESERVED and still absent.** `Ordered` sits on the server's enum and its own
 * recompute never produces it — a fully ordered request IS Complete — so naming it here would
 * be a state nothing can write.
 */
export const PR_STATUS = {
  draft: 0,
  submitted: 1,
  approved: 2,
  rejected: 3,
  cancelled: 4,
  partiallyComplete: 6,
  complete: 7,
} as const;

export type PurchaseRequestStatus = (typeof PR_STATUS)[keyof typeof PR_STATUS];

/**
 * Where ONE line sits between being ordered and being delivered.
 *
 * **A different axis from `PR_STATUS` beside it**: that one says how much of the request has
 * been turned into an ORDER, and this says how much of a line has actually arrived. A request
 * reading `Fully ordered` can have every line of it still Awaiting delivery.
 *
 * **The VALUE is what the column sorts on and the server-sent LABEL is what it renders.**
 * Alphabetically the four read Awaiting delivery, Fully received, Not ordered, Partially
 * received — alphabetical order pretending to be a pipeline.
 */
export const PR_LINE_DELIVERY = {
  notOrdered: 0,
  awaitingDelivery: 1,
  partiallyReceived: 2,
  fullyReceived: 3,
} as const;

export type PurchaseRequestLineDelivery =
  (typeof PR_LINE_DELIVERY)[keyof typeof PR_LINE_DELIVERY];

/**
 * Whether this urgency has to state a justification.
 *
 * **Mirrors the server's `PurchaseRequestUrgencies.RequiresJustification`**, and the detail DTO
 * also SENDS the answer as `justificationRequired` — this exists for the CREATE form, which has
 * no record to read it off yet. Two copies of one rule, which is why the server checks it in
 * both its own layers: the client's is a courtesy so the reader is not waiting on a round trip.
 */
export function requiresJustification(urgency: PurchaseRequestUrgency): boolean {
  return urgency === PR_URGENCY.critical || urgency === PR_URGENCY.emergency;
}

export interface PurchaseRequestListItem {
  id: string;
  requestNumber: string;
  urgency: PurchaseRequestUrgency;
  urgencyLabel: string;
  status: PurchaseRequestStatus;
  statusLabel: string;
  /** The distinct categories the lines fall under, joined. Resolved live from the lines. */
  categories: string | null;
  /** The distinct PRODUCTS the lines name, joined — concatenated into the anchor. */
  items: string | null;
  deliverToName: string | null;
  lineCount: number;
  /**
   * How many lines a purchase order covers IN FULL — COMMITMENT, draft orders included.
   *
   * It is what stops a line being ordered twice, and it is NOT what the ring reads.
   */
  orderedLineCount: number;
  /** How many lines a GOODS RECEIPT has covered in full — the ring. */
  /** How much of what was requested has actually landed — the ring's numerator, capped. */
  receivedQuantity: number;
  totalQuantity: number;
  /** Null where no line carries a price — not the same claim as zero. */
  estimatedTotal: number | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/**
 * How far through FULFILMENT a request is — or null, where that is not a question yet.
 *
 * **ONE derivation, read by both views.** The list row draws it as a ring and the table states
 * the same percentage in its `Fulfilled` column; written twice, the two would answer one
 * request differently the first time either changed, and each screen would look correct.
 *
 * **NULL is not a progress of zero**, and the distinction is the whole of what this function
 * decides. A draft nobody has approved, one awaiting a decision, one that was rejected and one
 * somebody cancelled are not 0% fulfilled — they are outside the question, and a ring on them
 * would put an empty circle down most of the register claiming nothing is happening to records
 * where nothing can. Fulfilment starts at APPROVED, which is the first state a purchase order
 * may draw on, and `lineCount` of zero returns null for the same reason: nothing to be a
 * fraction of is a question with no answer rather than an answer of none.
 *
 * **IT COUNTS WHAT HAS ARRIVED** — product call, 05 Oct 2026, in the reader's own words:
 * *the % should indicate whether the items were already been received in the Goods receipt.*
 *
 * **THAT IS THE THIRD READING OF ONE FIGURE, and the first two are recorded rather than
 * deleted.** The ring has walked the pipeline forward, one instruction at a time:
 *
 * ```
 * orderedLineCount   any order covers the line — commitment, draft included
 * sentLineCount      a DISPATCHED order covers it — it reached the supplier
 * receivedLineCount  a goods receipt has covered it IN FULL — it arrived
 * receivedQuantity   HOW MUCH of it arrived — what the ring reads now
 * ```
 *
 * Each was right about the question it was asked. `orderedLineCount` read 100% on a request
 * whose every line sat on an unsent draft; `sentLineCount` answered that and still read 100%
 * on an order nothing had been delivered against.
 *
 * **`sentLineCount` WENT WITH THE CHANGE, because nothing else read it** — a value crossing the
 * wire for nobody is the `ExpiryThresholdDays` failure one layer out. `orderedLineCount` stays
 * precisely because it DOES have another reader: it is COMMITMENT, which is what the order
 * wizard's pool reads as "left to order", and narrowing that would let one line be ordered
 * twice.
 *
 * **The ring may therefore go DOWN only by an order being DELETED**, never by one being
 * cancelled: cancelling releases a commitment and destroys nothing, so goods that physically
 * arrived stay counted.
 */
export function fulfilmentOf(
  row: PurchaseRequestListItem,
): { done: number; total: number } | null {
  const ORDERABLE: PurchaseRequestStatus[] = [
    PR_STATUS.approved,
    PR_STATUS.partiallyComplete,
    PR_STATUS.complete,
  ];

  if (!ORDERABLE.includes(row.status) || row.lineCount <= 0) return null;

  return { done: row.receivedQuantity, total: row.totalQuantity };
}

export interface PurchaseRequestLine {
  id: string;
  inventoryItemId: string;
  sku: string;
  itemName: string;
  unit: string;
  categoryName: string | null;
  requestedQuantity: number;
  estimatedUnitCost: number | null;
  lineTotal: number | null;
  justification: string | null;
  /** The live purchase orders covering this line, joined — null where none does. */
  orderNumbers: string | null;
  /** Derived server-side from the commitment and what the goods receipts accepted. */
  deliveryStatus: PurchaseRequestLineDelivery;
  /** That state in words. The enum crosses the wire as a NUMBER, so nothing renders it raw. */
  deliveryStatusLabel: string;
  isActive: boolean;
}

export interface PurchaseRequestDetail {
  id: string;
  requestNumber: string;
  urgency: PurchaseRequestUrgency;
  urgencyLabel: string;
  /** Sent, so the form's required marker and the endpoint's refusal cannot disagree. */
  justificationRequired: boolean;
  status: PurchaseRequestStatus;
  statusLabel: string;
  /** A Draft, and nothing else. Derived server-side. */
  isEditable: boolean;
  deliverToStorageFacilityId: string | null;
  deliverToName: string | null;
  justification: string | null;
  notes: string | null;
  cancellationReason: string | null;
  quotationRepositoryItemId: string | null;
  items: PurchaseRequestLine[];
  estimatedTotal: number | null;
  totalQuantity: number;
  approvalStatus: ApprovalRequestStatus | null;
  /** That decision in words. The enum crosses the wire as a NUMBER, so nothing renders it raw. */
  approvalStatusLabel: string | null;
  approvalRequestId: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface PurchaseRequestListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  stage?: string;
  urgency?: string;
  facility?: string;
}

/** One catalog row a line may name. */
export interface PurchaseRequestItemOption {
  id: string;
  sku: string;
  /** The item TYPE's name — `Laptop`, not `Lenovo ThinkPad X1`. */
  name: string;
  unit: string;
  categoryName: string | null;

  /*
    THE PRODUCT, which is what the picker's label states. `name` is the governed type, so a
    catalog holding three laptops offers it three times and the reader cannot tell which is
    which — these three are the same fields the Stock Levels anchor tells them apart by, and
    `lib/itemIdentity` is the one place they are joined.
  */
  manufacturer: string | null;
  brandName: string | null;
  model: string | null;

  /** What the last priced lot cost. The catalog itself carries no unit cost. */
  lastUnitCost: number | null;
}

/**
 * One line as the form sends it.
 *
 * **No id.** The set is addressed as a whole and reconciled server-side, so a line has nothing
 * for the client to address it by.
 */
export interface PurchaseRequestLineInput {
  inventoryItemId: string;
  requestedQuantity: number;
  estimatedUnitCost: number | null;
  justification: string | null;
}

export interface PurchaseRequestRequest {
  urgency: PurchaseRequestUrgency;
  deliverToStorageFacilityId: string | null;
  justification: string | null;
  notes: string | null;
  items: PurchaseRequestLineInput[];
  /** Says what the reader intends to do NEXT. Never sent on an update. */
  submitImmediately?: boolean;
}

/**
 * The urgency options, in order of severity.
 *
 * `value` is a STRING because that is what `<Select>` binds; the caller converts with
 * `Number(next)`, which is the pattern the issuance form's own method picker already uses.
 * `sort="none"` keeps them in this order — the whole point of an urgency list is that it reads
 * top to bottom.
 */
export const URGENCY_OPTIONS: { value: string; label: string; sublabel: string }[] = [
  {
    value: String(PR_URGENCY.normal),
    label: 'Normal',
    sublabel: 'The ordinary procurement cadence',
  },
  {
    value: String(PR_URGENCY.urgent),
    label: 'Urgent',
    sublabel: 'Needed ahead of the usual cycle',
  },
  {
    value: String(PR_URGENCY.critical),
    label: 'Critical',
    sublabel: 'Jumps the cadence — a justification is required',
  },
  {
    value: String(PR_URGENCY.emergency),
    label: 'Emergency',
    sublabel: 'Jumps the cadence — a justification is required',
  },
];

/** The urgency's own word, for the one place the server does not send a label: the create form. */
export const URGENCY_LABEL: Record<PurchaseRequestUrgency, string> = {
  [PR_URGENCY.normal]: 'Normal',
  [PR_URGENCY.urgent]: 'Urgent',
  [PR_URGENCY.critical]: 'Critical',
  [PR_URGENCY.emergency]: 'Emergency',
};
